import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import Graphic from '@arcgis/core/Graphic';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import GroupLayer from '@arcgis/core/layers/GroupLayer';
import { finalize, Subject, Subscription, takeUntil } from 'rxjs';
import { LoadingController } from 'app/shared/helpers/loading.controller';
import { CodedValueItem } from 'app/shared/interfaces/coded-value-item.interface';
import { EntityModelLike } from 'app/shared/models/entity.model';
import {
  OccurrenceModel,
  OccurrenceTypeModelLike,
  StatusOccurrenceLike,
} from 'app/shared/models/occurrence.model';
import { EntityService } from 'app/shared/services/entity.service';
import { MainMapService } from 'app/shared/components/main-map/main-map.service';
import {
  OccurrenceService,
  SearchOccurrenceFilterLike,
} from 'app/shared/services/occurrence.service';
import { ToastService } from 'app/shared/services/toast.service';
import { EsriUtils } from 'app/shared/utils/esri.utils';
import { measurement } from '../../../../default-symbols';
import Viewpoint from '@arcgis/core/Viewpoint';
import { ScreenService } from 'app/shared/services/screen.service';
import { TranslationService } from '@services/translation.service';

interface SearchOccurrenceFormLike {
  status: FormControl<string>;
  idSiia: FormControl<string>;
  type: FormControl<string>;
  entityId: FormControl<string>;
  entityIdNotified: FormControl<string>;
  startDate: FormControl<Date>;
  endDate: FormControl<Date>;
}

@Component({
  selector: 'app-search-occurrence',
  templateUrl: 'search-occurrence.component.html',
  styleUrls: ['search-occurrence.component.scss'],
})
export class SearchOccurrenceComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  mapView: __esri.MapView;
  @Input()
  mainMapImageLayer: __esri.MapImageLayer;
  @Input()
  groupLayer: __esri.GroupLayer;

  editGroupLayer: __esri.GroupLayer;
  private zoomToGraphicsLayer: __esri.GraphicsLayer;

  formGroup: FormGroup<SearchOccurrenceFormLike>;

  filterObj: SearchOccurrenceFilterLike;
  resultList: OccurrenceModel[];

  modelToEdit: OccurrenceModel;

  modelZoomToSelected: OccurrenceModel;

  statusList: StatusOccurrenceLike[];
  typeList: OccurrenceTypeModelLike[];
  entityList: EntityModelLike[];

  totalCount: number;
  pageIndex: number = 0;
  pageSize: number = 10;

  private pagesCache = new Map<number, any[]>();

  loadingController = new LoadingController();

  private searchResultsSubscription: Subscription;

  private mapViewSubscription: Subscription;

  isMobileModeObservable = this.screenService.observeIsMobile();

  private lifecycle = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private occurrenceService: OccurrenceService,
    private entityService: EntityService,
    private mainMapService: MainMapService,
    private toastService: ToastService,
    private globalLoadingController: LoadingController,
    private screenService: ScreenService,
    private ts: TranslationService
  ) {
    this.formGroup = this.formBuilder.group({
      status: this.formBuilder.control<string>(null),
      idSiia: this.formBuilder.control<string>(null),
      type: this.formBuilder.control<string>(null),
      entityId: this.formBuilder.control<string>(null),
      entityIdNotified: this.formBuilder.control<string>(null),
      startDate: this.formBuilder.control<Date>(null),
      endDate: this.formBuilder.control<Date>(null),
    });
  }

  ngOnInit(): void {
    this.globalLoadingController.show();
    this.occurrenceService
      .getOccurrenceStatusList()
      .pipe(
        finalize(() => {
          this.globalLoadingController.hide();
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe({
        next: (list) => {
          this.statusList = list;
        },
        error: (response) => {
          if (!response) {
            return;
          }

          this.toastService.error(
            this.ts.getCurrentLanguage()['failed-load-incident-statuses']
          );
        },
      });

    this.globalLoadingController.show();
    this.occurrenceService
      .getOccurrenceTypeList()
      .pipe(
        finalize(() => {
          this.globalLoadingController.hide();
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe({
        next: (list) => {
          this.typeList = list;
        },
        error: (response) => {
          if (!response) {
            return;
          }

          this.toastService.error(
            this.ts.getCurrentLanguage()['failed-load-incident-types']
          );
        },
      });

    this.globalLoadingController.show();
    this.entityService
      .listAllEntities()
      .pipe(
        finalize(() => {
          this.globalLoadingController.hide();
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe({
        next: (list) => {
          this.entityList = list;
        },
        error: (response) => {
          if (!response) {
            return;
          }

          this.toastService.error(
            this.ts.getCurrentLanguage()['failed-load-entities']
          );
        },
      });

    this.mainMapService.onRequestClear
      .pipe(takeUntil(this.lifecycle))
      .subscribe(() => {
        this.modelToEdit = null;

        this.formGroup.reset();

        this.filterObj = null;
        this.loadResults(true);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.mapView || changes.groupLayer) {
      this.handleMapView();
    }
  }

  ngOnDestroy(): void {
    this.lifecycle.next();
    this.lifecycle.complete();

    this.mapViewSubscription?.unsubscribe();
  }

  onSubmit(e) {
    // @note SUPANAC029 Aqui é onde pesquisa a ocorrência
    e?.preventDefault();

    this.filterObj = this.formGroup.getRawValue();

    this.loadResults(true);
  }

  onPageChange(pageIndex: number) {
    const lastPageIndex =
      Math.ceil((this.totalCount ?? 0) / this.pageSize) - 1 || 0;
    const newPageIndex =
      lastPageIndex <= -1
        ? -1
        : Math.max(0, Math.min(pageIndex, lastPageIndex));

    if (newPageIndex !== this.pageIndex) {
      this.pageIndex = newPageIndex;

      this.loadResults();
    }
  }

  onBtnGoBackClick() {
    this.modelToEdit = null;
  }

  onBtnZoomToClick(item: OccurrenceModel) {
    // @todo SUPANAC027 (feat: highlight) Buscar ocorrência por ID
    // OBJETIVO: Pegar a área da ocorrência para dar highlight,
    // igual ocorre na função onEditClick

    const point = EsriUtils.geometryFromJson(item.geometry) as __esri.Point;
    if (point) {
      if (this.zoomToGraphicsLayer) {
        this.zoomToGraphicsLayer.removeAll();
        const graphic = new Graphic({
          geometry: point,
          symbol: measurement.pointSymbolDefault,
        });
        this.zoomToGraphicsLayer.add(graphic);
      }

      this.mainMapService.goTo(
        new Viewpoint({
          targetGeometry: point,
          scale: 1128,
        })
      );

      this.modelZoomToSelected = item;
    }
  }

  // @note EDIÇÃO DA OCORRÊNCIA
  onEditClick(item: OccurrenceModel) {
    if (this.globalLoadingController.isShown()) {
      return;
    }

    this.globalLoadingController.show();
    this.occurrenceService
      .getById(item.id)
      .pipe(
        finalize(() => {
          this.globalLoadingController.hide();
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe({
        next: (model) => {
          this.modelToEdit = model;
        },
        error: (response) => {
          if (!response) {
            return;
          }

          this.toastService.error(
            this.ts.getCurrentLanguage()['failed-fetch-incident-data']
          );
        },
      });
  }

  onOccurenceSaved(e) {
    this.modelToEdit = null;
  }

  private loadResults(reloadMode = false) {
    this.searchResultsSubscription?.unsubscribe();
    if (reloadMode) {
      this.pageIndex = 0;
      this.totalCount = 0;
      this.resultList = null;
      this.pagesCache.clear();
    }

    if (!this.filterObj) {
      return;
    }

    if (this.pagesCache.has(this.pageIndex)) {
      this.resultList = this.pagesCache.get(this.pageIndex);
      return;
    }

    const skip = this.pageIndex * this.pageSize;

    this.loadingController.show();
    this.searchResultsSubscription = this.occurrenceService
      .queryList(this.filterObj, this.pageSize, skip)
      .pipe(
        finalize(() => {
          this.loadingController.hide();
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe({
        next: (res) => {
          this.resultList = res?.data;
          this.totalCount = res?.totalCount ?? this.resultList?.length;

          this.pagesCache.set(this.pageIndex, this.resultList);
        },
        error: (response) => {
          if (!response) {
            return;
          }

          this.toastService.error(
            this.ts.getCurrentLanguage()['failed-load-results']
          );
        },
      });
  }

  private handleMapView() {
    this.mapViewSubscription?.unsubscribe();
    if (!this.mapView) {
      return;
    }

    const layersMixin: __esri.LayersMixin = this.groupLayer ?? this.mapView.map;
    this.editGroupLayer = new GroupLayer();
    layersMixin.add(this.editGroupLayer);

    this.zoomToGraphicsLayer = new GraphicsLayer();
    layersMixin.add(this.zoomToGraphicsLayer);

    this.mapViewSubscription = new Subscription(() => {
      layersMixin.remove(this.editGroupLayer);
      this.editGroupLayer?.destroy();

      layersMixin.remove(this.zoomToGraphicsLayer);
      this.zoomToGraphicsLayer?.destroy();
    });
  }
}
